import { Injectable } from '@angular/core';

import { Observable, map, tap } from 'rxjs';

import {
  Account,
  CommonStoreConfiguration,
  Customer,
  Employee,
  EmployeeType,
  ErrorPolicy,
  FleetMasterContract,
  ToolListColumn,
  ToolRepairField,
  TpmStoreConfiguration
} from '@hdms/shared/common-types';
import {
  FT_TPM_BY_DESIGN,
  UserContextService,
  getAccount,
  getFleetMasterContact,
  getLocalization
} from '@hdms/shared/data-access';
import { EnvironmentService, FeatureToggleService } from '@hdms/shared/util-core';

import { TpmContextGqlService } from './tpm-context-gql.service';

export const FT_TPM_REPAIR_TRACKING_IMPROVEMENTS_MVP3 = 'ldt_tpm_hdms_repair_tracking_improvements_mvp3';

@Injectable({
  providedIn: 'root'
})
export class AccessService {
  private storeConfig: TpmStoreConfiguration;
  private commonConfig: CommonStoreConfiguration;
  private companyAccount: Account;
  private fleetMasterContract: FleetMasterContract;
  private employeeType: EmployeeType;

  constructor(
    private readonly contextGqlService: TpmContextGqlService,
    private readonly envService: EnvironmentService,
    private readonly ftService: FeatureToggleService,
    private readonly userContextService: UserContextService
  ) {}

  getData$(): Observable<boolean> {
    return this.contextGqlService
      .fetch(
        { localization: getLocalization(this.envService), isByDesign: this.envService.isByDesign },
        { errorPolicy: ErrorPolicy.IGNORE }
      )
      .pipe(
        map(({ data }) => data),
        tap(({ storeConfiguration: { toolParkManagement, common }, loggedInContext }) => {
          this.storeConfig = toolParkManagement;
          this.commonConfig = common;
          loggedInContext && this.setContextData(loggedInContext);
        }),
        map(_ => true)
      );
  }

  isByDesignMO(): boolean {
    return this.ftService.getToggleById(FT_TPM_BY_DESIGN);
  }

  isFleetContract(): boolean {
    return this.userContextService.hasFleetContract;
  }

  isAccountManager(): boolean {
    return this.employeeType === EmployeeType.ACCOUNT_MANAGER;
  }

  isLostStolenEnabled(): boolean {
    return this.storeConfig.lostStolenToolProcess && (this.isFleetContract() || this.isByDesignMO());
  }

  isInventoryNumberVisible(): boolean {
    return this.isFleetContract() && this.fleetMasterContract.fleetLabelingTemplate?.isInventoryNumberVisible;
  }

  isOrganizationalReferenceVisible(): boolean {
    return this.isFleetContract() && this.fleetMasterContract.fleetLabelingTemplate?.isOrganizationalReferenceVisible;
  }

  isReorderLabelsEnabled(): boolean {
    return this.isInventoryNumberVisible() || this.isOrganizationalReferenceVisible();
  }

  isReturnEnabled(): boolean {
    return this.isFleetContract() && this.storeConfig.returnToolProcess;
  }

  isFleetExchangeEnabled(): boolean {
    return (
      this.storeConfig.fleetExchangeToolProcess &&
      (this.isFleetReturnEnabled() || this.isFleetRenewEnabled() || this.isFleetExtendEnabled())
    );
  }

  isFleetReturnEnabled(): boolean {
    return (
      this.isFleetContract() &&
      this.storeConfig.returnToolProcess &&
      (this.isAccountManager() ? this.storeConfig.employeeFleetExchangeReturnToolProcess : true)
    );
  }

  isFleetRenewEnabled(): boolean {
    return (
      this.isFleetContract() &&
      this.storeConfig.fleetExchangeRenewToolProcess &&
      (this.isAccountManager() ? this.storeConfig.employeeFleetExchangeRenewToolProcess : true)
    );
  }

  isFleetExtendEnabled(): boolean {
    return (
      this.isFleetContract() &&
      this.storeConfig.fleetExchangeExtendToolProcess &&
      (this.isAccountManager() ? this.storeConfig.employeeFleetExchangeExtendToolProcess : true)
    );
  }

  isLoanEnabled(): boolean {
    return this.isFleetContract() && this.storeConfig.loanToolProcess;
  }

  isPeakEnabled(): boolean {
    return this.isFleetContract() && this.storeConfig.peakToolProcess;
  }

  isCircularityReportsEnabled(): boolean {
    return this.isFleetContract() && this.storeConfig.circularityReports;
  }

  isCostCenterEnabled(): boolean {
    return this.isFleetContract() && this.fleetMasterContract.isCostCenterRequired;
  }

  isMonthlyFeeColumnEnabled(): boolean {
    return this.isFleetContract() && this.canViewCustomerSpecificPricing();
  }

  isPurchaseOrderNumberEnabled(): boolean {
    return this.storeConfig.toolRepairFields.includes(ToolRepairField.PURCHASE_ORDER_NUMBER);
  }

  isPurchaseOrderDocumentEnabled(): boolean {
    return this.storeConfig.toolRepairFields.includes(ToolRepairField.PURCHASE_ORDER_DOCUMENT);
  }

  showRepairTrackingDates(): boolean {
    return this.storeConfig.showRepairTrackingDates;
  }

  isToolDocumentsDownloadEnabled(): boolean {
    return this.storeConfig.toolDocumentsDownload;
  }

  isTpmEnabled(): boolean {
    return this.storeConfig.toolParkManagement;
  }

  isRepairTrackingEnabled(): boolean {
    return this.storeConfig.repairTrackingProcess;
  }

  isPoliceReportForStolenToolsRequired(): boolean {
    return this.storeConfig.stolenToolsPoliceReportRequired;
  }

  isChangeCostCenterProcessEnabled(): boolean {
    return this.isAccountManager() ? this.storeConfig.employeeChangeCostCenterToolProcess : true;
  }

  getPoliceReportExtensionsForStolenTools(): string[] {
    return this.storeConfig.stolenToolsPoliceReportFileExtensions;
  }

  isOathDeclarationForLostToolsRequired(): boolean {
    return this.storeConfig.lostToolsOathDeclarationRequired;
  }

  getToolListColumns(): ToolListColumn[] {
    return this.storeConfig.toolListColumns;
  }

  getAccountId(): string {
    return this.companyAccount.id;
  }

  getAccount(): Account {
    return this.userContextService.account;
  }

  getToolPickupAndDeliveryOptions(): boolean {
    return this.storeConfig.toolPickupAndDeliveryOptions;
  }

  getShowPickUpDate(): boolean {
    return this.storeConfig.showPickUpDate;
  }

  canViewCustomerSpecificPricing(): boolean {
    // TODO: just return true as long auth-mgmt is not re-implemented
    return true;
  }

  getAdditionalPackagingDays(): number {
    return this.storeConfig.additionalPackagingDays;
  }

  getRestrictedDates(): Date[] {
    return (this.commonConfig.restrictedDates ?? []).map(date => new Date(date));
  }

  hasTermsAndConditionsCheckbox(): boolean {
    return this.commonConfig.termsAndConditions;
  }

  getFleetMasterContractId(): string {
    return this.fleetMasterContract.id;
  }

  repairTrackingImprovementMvp3Enabled(): boolean {
    return this.ftService.getToggleById(FT_TPM_REPAIR_TRACKING_IMPROVEMENTS_MVP3);
  }

  private setContextData(ctx: Customer | Employee): void {
    this.companyAccount = getAccount(ctx);
    this.fleetMasterContract = getFleetMasterContact(ctx);
    this.employeeType = (ctx as Employee).type;
  }
}
